import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Box, Typography } from "@mui/material";

const normalizeSubCode = (subCode) => {
  if (subCode === null || subCode === undefined || subCode === "") {
    return "98";
  }

  return String(subCode);
};

const SupplierProductNotesOverlay = ({ open, supplierId, claimProductTypeCode, claimProductSubCode }) => {
  const [matchedSupplierNotes, setMatchedSupplierNotes] = useState([]);
  const [noteOverlayPosition, setNoteOverlayPosition] = useState({ top: 16, left: null });
  const [isNoteOverlayDragging, setIsNoteOverlayDragging] = useState(false);
  const noteOverlayDragOffsetRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!open || !supplierId || !claimProductTypeCode || !claimProductSubCode) {
      setMatchedSupplierNotes([]);
      return undefined;
    }

    let isCancelled = false;
    const fetchMatchedNotes = async () => {
      try {
        const response = await axios.get("/api/v1/supplierProductCombinationNotes/match", {
          params: {
            supplierId,
            categoryCode: String(claimProductTypeCode),
            productSubCode: normalizeSubCode(claimProductSubCode),
          },
        });

        if (!isCancelled) {
          setMatchedSupplierNotes(response.data ?? []);
        }
      } catch (error) {
        if (!isCancelled) {
          setMatchedSupplierNotes([]);
        }
      }
    };

    void fetchMatchedNotes();

    return () => {
      isCancelled = true;
    };
  }, [claimProductSubCode, claimProductTypeCode, open, supplierId]);

  useEffect(() => {
    if (noteOverlayPosition.left !== null) {
      return;
    }

    const overlayWidth = 350;
    const centeredLeft = Math.max(8, Math.round((window.innerWidth - overlayWidth) / 2));
    setNoteOverlayPosition((prev) => ({ ...prev, left: centeredLeft }));
  }, [noteOverlayPosition.left]);

  useEffect(() => {
    if (!isNoteOverlayDragging) {
      return undefined;
    }

    const handleMouseMove = (event) => {
      const overlayWidth = 350;
      const minPadding = 8;
      const nextLeftRaw = event.clientX - noteOverlayDragOffsetRef.current.x;
      const nextTopRaw = event.clientY - noteOverlayDragOffsetRef.current.y;
      const maxLeft = Math.max(minPadding, window.innerWidth - overlayWidth - minPadding);

      setNoteOverlayPosition({
        left: Math.min(maxLeft, Math.max(minPadding, nextLeftRaw)),
        top: Math.max(minPadding, nextTopRaw),
      });
    };

    const handleMouseUp = () => {
      setIsNoteOverlayDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isNoteOverlayDragging]);

  const handleStartDraggingNoteOverlay = useCallback((event) => {
    if (event.button !== 0) {
      return;
    }

    const overlayElement = event.currentTarget.parentElement;
    if (!overlayElement) {
      return;
    }

    const overlayRect = overlayElement.getBoundingClientRect();
    noteOverlayDragOffsetRef.current = {
      x: event.clientX - overlayRect.left,
      y: event.clientY - overlayRect.top,
    };
    setIsNoteOverlayDragging(true);
    event.preventDefault();
  }, []);

  if (!open || matchedSupplierNotes.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: noteOverlayPosition.top,
        left: noteOverlayPosition.left ?? "50%",
        transform: noteOverlayPosition.left === null ? "translateX(-50%)" : "none",
        zIndex: 1600,
        width: 350,
        minHeight: 150,
        maxHeight: "45vh",
        overflowY: "auto",
        border: "1px solid",
        borderColor: "#ed6c02",
        borderLeftWidth: 8,
        borderRadius: 1.5,
        backgroundColor: "#fff4e5",
        boxShadow: 6,
        px: 2,
        py: 1.5,
        color: "#663c00 !important",
        "& *": {
          color: "#663c00 !important",
        },
      }}
    >
      <Box
        onMouseDown={handleStartDraggingNoteOverlay}
        sx={{
          cursor: isNoteOverlayDragging ? "grabbing" : "grab",
          userSelect: "none",
          mb: 1,
          mx: -2,
          mt: -1.5,
          px: 2,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "#ed6c02",
          backgroundColor: "#fff4e5",
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        }}
      >
        <Typography variant="subtitle2" sx={{ color: "#663c00 !important", fontWeight: 700 }}>
          Supplier Product Notes
        </Typography>
      </Box>
      {matchedSupplierNotes.map((note) => (
        <Typography
          key={note.supplierProductCombinationNoteId}
          variant="body2"
          sx={{ mb: 0.8, whiteSpace: "pre-wrap", color: "#1f1f1f !important" }}
        >
          {note.messageText}
        </Typography>
      ))}
    </Box>
  );
};

export default SupplierProductNotesOverlay;
